import { useCallback, useEffect, useState } from 'react';
import { Button, Input, Spinner } from '../design-system';
import {
  APP_STORE_URL,
  PLAY_STORE_URL,
  RELEASE_BODY,
  RELEASE_TITLE,
  announceRelease,
  cleanReleaseVersion,
  loadAnnouncements,
  loadReleaseSettings,
  previewAnnouncement,
  saveReleaseSettings,
  type Announcement,
  type Reach,
  type ReleaseSettings,
} from '../../lib/releaseAnnouncement';

const OUTCOME: Record<Announcement['outcome'], { label: string; tone: string }> = {
  sending: { label: 'Sending…', tone: 'text-app-muted' },
  sent: { label: 'Sent', tone: 'text-green-700 dark:text-green-300' },
  partial: { label: 'Sent, with problems', tone: 'text-amber-700 dark:text-amber-300' },
  failed: { label: 'Failed', tone: 'text-red-700 dark:text-red-300' },
  empty: { label: 'Nobody to tell', tone: 'text-app-muted' },
};

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * Admin → Push notifications → "New version is out". A switch (ships Off), the two store
 * links, and a button that tells everyone whose app is older than the version you type.
 * The message is fixed; tapping it opens the phone's store page.
 */
export function ReleaseAnnouncement() {
  const [settings, setSettings] = useState<ReleaseSettings | null>(null);
  const [history, setHistory] = useState<Announcement[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState<'switch' | 'links' | 'send' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [appStore, setAppStore] = useState('');
  const [playStore, setPlayStore] = useState('');
  const [version, setVersion] = useState('');
  const [reach, setReach] = useState<Reach | null>(null);
  const [latest, setLatest] = useState<Reach['latest']>(null);
  const [latestLoaded, setLatestLoaded] = useState(false);
  const [checking, setChecking] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [recount, setRecount] = useState(0); // bumped to count again (e.g. after the links change)

  const load = useCallback(async () => {
    const [s, h] = await Promise.all([loadReleaseSettings(), loadAnnouncements()]);
    setSettings(s);
    setHistory(h);
    if (s) { setAppStore(s.app_store_url ?? ''); setPlayStore(s.play_store_url); }
    setLoaded(true);
  }, []);
  useEffect(() => { void load(); }, [load]);

  const cleanVersion = cleanReleaseVersion(version);
  const on = !!settings?.announce_releases;

  // Find the newest version any phone reports, and prefill the box with it (the admin can change it).
  useEffect(() => {
    if (!on) { setLatest(null); setLatestLoaded(false); return; }
    let cancelled = false;
    void previewAnnouncement().then((r) => {
      if (cancelled) return;
      setLatest(r.latest);
      setLatestLoaded(true);
      if (r.latest) setVersion((v) => (v.trim() ? v : r.latest!.version));
    }).catch(() => { if (!cancelled) setLatestLoaded(true); });
    return () => { cancelled = true; };
  }, [on]);

  // Count who it would reach once the version looks right and the switch is on.
  useEffect(() => {
    setReach(null);
    setConfirming(false);
    if (!on || !cleanVersion) return;
    let cancelled = false;
    setChecking(true);
    const t = setTimeout(async () => {
      try {
        const r = await previewAnnouncement(cleanVersion);
        if (!cancelled) { setReach(r); setError(null); }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not check who it reaches.');
      }
      if (!cancelled) setChecking(false);
    }, 400);
    return () => { cancelled = true; clearTimeout(t); setChecking(false); };
  }, [on, cleanVersion, recount]);

  async function toggle() {
    if (!settings) return;
    setBusy('switch');
    setError(null);
    setNotice(null);
    try {
      await saveReleaseSettings(settings.id, { announce_releases: !on });
      setSettings({ ...settings, announce_releases: !on });
    } catch {
      setError("Couldn't save the switch. Please try again.");
    }
    setBusy(null);
  }

  const appStoreTrim = appStore.trim();
  const playStoreTrim = playStore.trim();
  const appStoreBad = appStoreTrim !== '' && !APP_STORE_URL.test(appStoreTrim);
  const playStoreBad = !PLAY_STORE_URL.test(playStoreTrim);
  const linksChanged = !!settings && (appStoreTrim !== (settings.app_store_url ?? '') || playStoreTrim !== settings.play_store_url);

  async function saveLinks() {
    if (!settings || appStoreBad || playStoreBad) return;
    setBusy('links');
    setError(null);
    setNotice(null);
    try {
      await saveReleaseSettings(settings.id, { app_store_url: appStoreTrim || null, play_store_url: playStoreTrim });
      setSettings({ ...settings, app_store_url: appStoreTrim || null, play_store_url: playStoreTrim });
      setNotice('Store links saved.');
      setRecount((n) => n + 1);
    } catch {
      setError("Couldn't save the links. Please try again.");
    }
    setBusy(null);
  }

  async function send() {
    if (!cleanVersion) return;
    setBusy('send');
    setError(null);
    setNotice(null);
    try {
      const r = await announceRelease(cleanVersion);
      setNotice(
        r.outcome === 'empty'
          ? 'Nobody needs this version, so nothing was sent.'
          : `Sent: ${plural(r.sent, 'device', 'devices')} told${r.failed ? `, ${plural(r.failed, 'failure', 'failures')}` : ''}.`,
      );
      setVersion('');
      setConfirming(false);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong. Please try again.');
      setConfirming(false);
      await load();
    }
    setBusy(null);
  }

  if (!loaded) return <div className="py-4"><Spinner /></div>;
  if (!settings) return null;

  const total = (reach?.ios ?? 0) + (reach?.android ?? 0);

  return (
    <section className="bg-app-card rounded-xl border border-app-border p-5 flex flex-col gap-4 max-w-lg" aria-labelledby="release-push-title">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 id="release-push-title" className="text-sm font-semibold text-app-fg uppercase tracking-wide">New version is out</h2>
          <p className="text-sm text-app-muted mt-1">
            After a release goes live in the stores, tell everyone who is still on an older version.
            Off by default.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={on}
          aria-label="Announce new releases"
          disabled={busy === 'switch'}
          onClick={() => void toggle()}
          className={`shrink-0 relative h-7 w-12 rounded-full transition-colors disabled:opacity-60 ${on ? 'bg-gold' : 'bg-app-border'}`}
        >
          <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all ${on ? 'left-[22px]' : 'left-0.5'}`} />
        </button>
      </div>

      {error && <p role="alert" className="text-sm text-crimson">{error}</p>}
      {notice && <p role="status" className="text-sm text-green-700 dark:text-green-300">{notice}</p>}

      {on && (
        <>
          <details className="border-t border-app-border pt-4 group">
            <summary className="cursor-pointer text-sm font-medium text-app-fg-2 select-none">
              Store links <span className="text-app-muted font-normal">(optional)</span>
            </summary>
            <div className="flex flex-col gap-3 mt-3">
              <p className="text-xs text-app-muted">
                With a link, tapping the push opens that store page. Without one, the tap just opens the app.
                Nothing here is required.
              </p>
              <Input
                label="App Store link (iPhone)"
                placeholder="https://apps.apple.com/app/sekretly/id… (optional)"
                value={appStore}
                onChange={(e) => setAppStore(e.target.value)}
                error={appStoreBad ? 'This should be a link that starts with https://apps.apple.com/' : undefined}
              />
              <Input
                label="Google Play link (Android)"
                value={playStore}
                onChange={(e) => setPlayStore(e.target.value)}
                error={playStoreBad ? 'This should be a Google Play link, like https://play.google.com/store/apps/details?id=…' : undefined}
              />
              <div className="flex items-center gap-3">
                <Button size="sm" variant="secondary" disabled={!linksChanged || appStoreBad || playStoreBad || busy === 'links'} onClick={() => void saveLinks()}>
                  {busy === 'links' ? 'Saving…' : 'Save links'}
                </Button>
              </div>
            </div>
          </details>

          <div className="flex flex-col gap-3 border-t border-app-border pt-4">
            <Input
              label="Version that just went live"
              placeholder="e.g. 1.0.16"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              error={version.trim() !== '' && !cleanVersion ? 'Use numbers with dots, for example 1.0.16' : undefined}
            />

            {latest && (
              <p className="text-xs text-app-muted">
                Newest version phones report: <strong className="font-medium text-app-fg-2">{latest.version}</strong> ({plural(latest.devices, 'phone', 'phones')}).
                {' '}Filled in for you; change it if the store has a different one.
              </p>
            )}
            {latestLoaded && !latest && (
              <p className="text-xs text-app-muted">No phone has reported its version yet (it starts once the new app build is installed). You can type the version.</p>
            )}

            <div className="min-h-[20px] text-sm text-app-muted" aria-live="polite">
              {checking && <span className="inline-flex items-center gap-2"><Spinner size="sm" /> Checking who it reaches…</span>}
              {!checking && reach?.already && <span>Version {cleanVersion} was already announced.</span>}
              {!checking && reach && !reach.already && (
                total === 0
                  ? <span>Nobody to tell: every registered phone already has {cleanVersion} or newer.</span>
                  : <span>Will reach {plural(reach.ios, 'iPhone', 'iPhones')} and {plural(reach.android, 'Android phone', 'Android phones')} on an older version.</span>
              )}
            </div>

            {reach && !reach.already && total > 0 && (
              <div className="rounded-xl border border-app-border bg-app-bg/60 p-3">
                <p className="text-[10px] uppercase tracking-widest text-app-muted font-medium mb-1">What they will see (tap opens the app or its store page)</p>
                <p className="text-sm font-semibold text-app-fg">{RELEASE_TITLE}</p>
                <p className="text-sm text-app-muted">{RELEASE_BODY}</p>
              </div>
            )}

            <div className="flex items-center gap-3">
              {!confirming ? (
                <Button disabled={!cleanVersion || !reach || reach.already || total === 0 || checking || busy === 'send'} onClick={() => setConfirming(true)}>
                  Announce version {cleanVersion ?? ''}
                </Button>
              ) : (
                <>
                  <Button disabled={busy === 'send'} onClick={() => void send()}>
                    {busy === 'send' ? 'Sending…' : `Confirm: tell ${plural(total, 'device', 'devices')} now`}
                  </Button>
                  <Button variant="secondary" disabled={busy === 'send'} onClick={() => setConfirming(false)}>Cancel</Button>
                </>
              )}
            </div>
            <p className="text-xs text-app-muted">A version can be announced once. Do it when the release is live in the store, not just in TestFlight.</p>
          </div>
        </>
      )}

      {history.length > 0 && (
        <div className="border-t border-app-border pt-4">
          <p className="text-xs font-medium uppercase tracking-wide text-app-muted mb-2">Announced so far</p>
          <ul className="flex flex-col gap-2">
            {history.map((a) => (
              <li key={a.id} className="flex items-baseline justify-between gap-3 text-sm">
                <span className="text-app-fg font-medium">{a.version}</span>
                <span className="text-app-muted text-xs">
                  {new Date(a.created_at).toLocaleDateString()} · {a.ios_sent + a.android_sent} told
                  {a.ios_failed + a.android_failed > 0 ? `, ${a.ios_failed + a.android_failed} failed` : ''}
                </span>
                <span className={`text-xs ${OUTCOME[a.outcome].tone}`}>{OUTCOME[a.outcome].label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

export default ReleaseAnnouncement;
